/**
 * CAMPOS Y NUMERACIÓN DE LA ESTRUCTURA (23-10-2026, plan aprobado por Pablo).
 *
 * Copia del cliente. La del servidor es
 * supabase/functions/_shared/camposEstructura.ts y tiene que ser IGUAL salvo esta
 * cabecera (test src/__tests__/camposEstructura.test.ts): si se separan, la
 * pantalla enseña «P01» y el informe del asistente saca otro número.
 *
 * El caso es la hoja de Frutas Nijasol: un código fijo de etiquetado (P01, S07),
 * un número que cambia cada campaña (CAM 9 = 166, CAM 10 = 193) y, si un sector
 * lleva dos cultivos, un número por cultivo. Cada empresa define sus campos en
 * Ajustes → Estructura → «Campos y numeración» (tabla `estructura_campos`) y sus
 * valores viven en `estructura_valores`, uno por campo × sitio × periodo.
 *
 * PURO: sin React, sin red y sin imports, para que la misma copia corra en Deno.
 */

export type NivelCampo = "finca" | "nave" | "sector" | "cultivo";
export type TipoCampo = "texto" | "numero" | "numeracion";
export type VigenciaCampo = "fija" | "campana" | "anio";

export const NIVELES: readonly NivelCampo[] = ["finca", "nave", "sector", "cultivo"];
export const TIPOS: readonly TipoCampo[] = ["texto", "numero", "numeracion"];
export const VIGENCIAS: readonly VigenciaCampo[] = ["fija", "campana", "anio"];

export interface Numeracion {
  /** Lo que va delante del número: «P» → P01. */
  prefijo: string;
  /** Dígitos con ceros delante: 2 → 01, 0 → 1. */
  ceros: number;
  /** El primer número. */
  inicio: number;
  /** En un periodo nuevo: seguir desde el último del anterior o volver a `inicio`. */
  al_cambiar_periodo: "seguir" | "reiniciar";
  /** Contar seguido en toda la empresa o empezar de nuevo en cada finca. */
  alcance: "empresa" | "finca";
}

export interface CampoEstructura {
  id: string;
  etiqueta: string;
  nivel: NivelCampo;
  tipo: TipoCampo;
  vigencia: VigenciaCampo;
  en_informes: boolean;
  junto_al_nombre: boolean;
  numeracion: Numeracion | null;
  orden: number;
  archivado_en: string | null;
}

export interface ValorEstructura {
  campo_id: string;
  entidad_id: string;
  /** '' (fijo), 'campana:<uuid>' o 'anio:2026'. */
  periodo: string;
  valor: string;
  manual: boolean;
}

export interface CampanaPeriodo {
  id: string;
  name: string;
  /** null = de toda la empresa (vale para cualquier finca sin campaña propia). */
  location_id: string | null;
  start_date: string;
  end_date: string;
}

export const NUMERACION_POR_DEFECTO: Numeracion = {
  prefijo: "",
  ceros: 0,
  inicio: 1,
  al_cambiar_periodo: "reiniciar",
  alcance: "empresa",
};

const entero = (v: unknown, min: number, max: number, def: number): number => {
  const n = typeof v === "number" ? v : typeof v === "string" && v.trim() !== "" ? Number(v) : NaN;
  if (!Number.isFinite(n)) return def;
  return Math.min(max, Math.max(min, Math.trunc(n)));
};

/** La numeración tal como viene de la BD (jsonb), con valores sensatos si falta algo. */
export function leerNumeracion(raw: unknown): Numeracion {
  const o = raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const prefijo = typeof o.prefijo === "string" ? o.prefijo.trim().slice(0, 10) : "";
  return {
    prefijo,
    ceros: entero(o.ceros, 0, 6, NUMERACION_POR_DEFECTO.ceros),
    inicio: entero(o.inicio, 0, 999_999_999, NUMERACION_POR_DEFECTO.inicio),
    al_cambiar_periodo: o.al_cambiar_periodo === "seguir" ? "seguir" : "reiniciar",
    alcance: o.alcance === "finca" ? "finca" : "empresa",
  };
}

/** Una fila de `estructura_campos` → campo, o null si no se entiende (se ignora). */
export function leerCampo(row: Record<string, unknown> | null | undefined): CampoEstructura | null {
  if (!row || typeof row.id !== "string") return null;
  const nivel = row.nivel as NivelCampo;
  const tipo = row.tipo as TipoCampo;
  const vigencia = (row.vigencia ?? "fija") as VigenciaCampo;
  if (!NIVELES.includes(nivel) || !TIPOS.includes(tipo) || !VIGENCIAS.includes(vigencia)) return null;
  const etiqueta = typeof row.etiqueta === "string" ? row.etiqueta.trim() : "";
  if (!etiqueta) return null;
  return {
    id: row.id,
    etiqueta,
    nivel,
    tipo,
    // El nivel «cultivo» ya va por ciclo de cultivo: su vigencia es siempre fija.
    vigencia: nivel === "cultivo" ? "fija" : vigencia,
    en_informes: row.en_informes !== false,
    junto_al_nombre: row.junto_al_nombre === true,
    numeracion: tipo === "numeracion" ? leerNumeracion(row.numeracion) : null,
    orden: entero(row.orden, -1_000_000, 1_000_000, 0),
    archivado_en: typeof row.archivado_en === "string" ? row.archivado_en : null,
  };
}

/** Los campos vivos, en su orden (y por etiqueta si empatan). */
export function camposVivos(campos: ReadonlyArray<CampoEstructura>): CampoEstructura[] {
  return campos
    .filter((c) => !c.archivado_en)
    .sort((a, b) => a.orden - b.orden || a.etiqueta.localeCompare(b.etiqueta, "es", { numeric: true }));
}

// ── Periodos ────────────────────────────────────────────────────────────────

/**
 * La campaña que cubre `fecha` para una finca: primero una propia de la finca y,
 * si no hay, una de toda la empresa. Si se solapan, la que empezó más tarde.
 */
export function campanaDe(
  fecha: string,
  locationId: string | null,
  campanas: ReadonlyArray<CampanaPeriodo>,
): CampanaPeriodo | null {
  const dia = fecha.slice(0, 10);
  const cubre = campanas.filter((c) => c.start_date.slice(0, 10) <= dia && dia <= c.end_date.slice(0, 10));
  const masReciente = (lista: CampanaPeriodo[]) =>
    lista.sort((a, b) => b.start_date.localeCompare(a.start_date))[0] ?? null;
  const propia = locationId ? masReciente(cubre.filter((c) => c.location_id === locationId)) : null;
  return propia ?? masReciente(cubre.filter((c) => c.location_id === null));
}

/**
 * El periodo de un campo en una fecha: '' si es fijo, 'anio:2026' si va por año,
 * 'campana:<id>' si va por campaña, o null si es por campaña y ninguna cubre la
 * fecha (entonces no hay valor que enseñar).
 */
export function periodoDe(
  vigencia: VigenciaCampo,
  fecha: string,
  locationId: string | null,
  campanas: ReadonlyArray<CampanaPeriodo>,
): string | null {
  if (vigencia === "fija") return "";
  if (vigencia === "anio") return `anio:${fecha.slice(0, 4)}`;
  const c = campanaDe(fecha, locationId, campanas);
  return c ? `campana:${c.id}` : null;
}

/**
 * El periodo de antes, para «seguir» numerando: el año anterior, o la campaña
 * del mismo ámbito (la misma finca, o de empresa) que empezó justo antes.
 */
export function periodoAnterior(periodo: string, campanas: ReadonlyArray<CampanaPeriodo>): string | null {
  if (periodo.startsWith("anio:")) {
    const y = Number(periodo.slice(5));
    return Number.isFinite(y) ? `anio:${y - 1}` : null;
  }
  if (periodo.startsWith("campana:")) {
    const actual = campanas.find((c) => `campana:${c.id}` === periodo);
    if (!actual) return null;
    const antes = campanas
      .filter((c) => c.location_id === actual.location_id && c.start_date < actual.start_date)
      .sort((a, b) => b.start_date.localeCompare(a.start_date))[0];
    return antes ? `campana:${antes.id}` : null;
  }
  return null;
}

// ── Valores ─────────────────────────────────────────────────────────────────

const clave = (campoId: string, entidadId: string, periodo: string) => `${campoId}|${entidadId}|${periodo}`;

export type IndiceValores = Map<string, ValorEstructura>;

export function indexarValores(valores: ReadonlyArray<ValorEstructura>): IndiceValores {
  const m: IndiceValores = new Map();
  for (const v of valores) m.set(clave(v.campo_id, v.entidad_id, v.periodo ?? ""), v);
  return m;
}

export function valorDe(indice: IndiceValores, campoId: string, entidadId: string, periodo: string | null): string | null {
  if (periodo === null) return null;
  return indice.get(clave(campoId, entidadId, periodo))?.valor ?? null;
}

/**
 * Lo que la persona escribe, listo para guardar: recortado y, si el campo es un
 * número, con punto decimal. null = vacío (se borra el valor). Lanza
 * `valor_no_numerico` si un campo de número no lleva un número.
 */
export function normalizarValor(tipo: TipoCampo, raw: string | null | undefined): string | null {
  const v = (raw ?? "").trim().slice(0, 200);
  if (!v) return null;
  if (tipo === "numero") {
    // Como se escribe en España: con coma, los puntos son de miles («12.500,5»);
    // sin coma, un punto seguido de grupos de tres cifras también («12.500»).
    const sinEspacios = v.replace(/\s/g, "");
    const n = sinEspacios.includes(",")
      ? sinEspacios.replace(/\./g, "").replace(",", ".")
      : /^-?\d{1,3}(\.\d{3})+$/.test(sinEspacios)
        ? sinEspacios.replace(/\./g, "")
        : sinEspacios;
    if (!/^-?\d+(\.\d+)?$/.test(n)) throw new Error("valor_no_numerico");
    return n;
  }
  return v;
}

// ── Numeración ──────────────────────────────────────────────────────────────

export function formatoNumero(n: number, cfg: Numeracion): string {
  return `${cfg.prefijo}${String(n).padStart(cfg.ceros, "0")}`;
}

/** El número de un valor con este formato («P07» → 7), o null si no lo es. */
export function numeroDeValor(valor: string, cfg: Numeracion): number | null {
  let v = valor.trim();
  if (cfg.prefijo) {
    if (!v.toLowerCase().startsWith(cfg.prefijo.toLowerCase())) return null;
    v = v.slice(cfg.prefijo.length);
  }
  return /^\d+$/.test(v) ? Number(v) : null;
}

/** Un sitio a numerar, ya en su orden. `grupo` es la finca (para alcance «finca»). */
export interface SitioANumerar {
  id: string;
  grupo: string | null;
}

export interface Numerado {
  entidad_id: string;
  valor: string;
}

/**
 * Los números de un periodo, en el orden de `sitios`.
 *
 *   · Cada sitio ocupa su puesto aunque tenga un valor escrito a mano: ese valor
 *     se respeta (no sale en el resultado) y el contador sigue, para que el
 *     resto no se descuadre.
 *   · «seguir» parte del último número del periodo anterior (`anteriores`, los
 *     valores de ese periodo) y «reiniciar», de `inicio`.
 *   · Con alcance «finca», cada finca cuenta por su lado.
 */
export function numerar(
  sitios: ReadonlyArray<SitioANumerar>,
  cfg: Numeracion,
  actuales: ReadonlyMap<string, { valor: string; manual: boolean }>,
  anteriores: ReadonlyArray<{ entidad_id: string; valor: string }> = [],
  grupoDe: (entidadId: string) => string | null = () => null,
): Numerado[] {
  const g = (grupo: string | null) => (cfg.alcance === "finca" ? grupo ?? "" : "*");
  const ultimo = new Map<string, number>();
  if (cfg.al_cambiar_periodo === "seguir") {
    for (const a of anteriores) {
      const n = numeroDeValor(a.valor, cfg);
      if (n === null) continue;
      const k = g(grupoDe(a.entidad_id));
      ultimo.set(k, Math.max(ultimo.get(k) ?? -Infinity, n));
    }
  }
  const siguiente = new Map<string, number>();
  const out: Numerado[] = [];
  for (const s of sitios) {
    const k = g(s.grupo);
    const n = siguiente.get(k) ?? (ultimo.has(k) ? (ultimo.get(k) as number) + 1 : cfg.inicio);
    siguiente.set(k, n + 1);
    if (actuales.get(s.id)?.manual) continue;
    const valor = formatoNumero(n, cfg);
    if (actuales.get(s.id)?.valor === valor) continue;
    out.push({ entidad_id: s.id, valor });
  }
  return out;
}

// ── Orden de los sitios ─────────────────────────────────────────────────────

export interface FincaOrden { id: string; name: string }
export interface NaveOrden { id: string; name: string; location_id: string | null }
export interface SectorOrden { id: string; name: string; greenhouse_id: string }
export interface CicloOrden { id: string; block_id: string; start_date: string; crop_name: string }

const natural = (a: string, b: string) => a.localeCompare(b, "es", { numeric: true, sensitivity: "base" });

/**
 * Los sitios de un nivel en el orden en que se numeran: finca → nave → sector →
 * ciclo, por nombre «de persona» (Nave 2 antes que Nave 10) y los ciclos por
 * fecha de inicio. Cada uno con su finca como `grupo`.
 */
export function sitiosEnOrden(
  nivel: NivelCampo,
  d: {
    fincas: ReadonlyArray<FincaOrden>;
    naves: ReadonlyArray<NaveOrden>;
    sectores?: ReadonlyArray<SectorOrden>;
    ciclos?: ReadonlyArray<CicloOrden>;
  },
): SitioANumerar[] {
  const fincaNombre = new Map(d.fincas.map((f) => [f.id, f.name]));
  const fincas = [...d.fincas].sort((a, b) => natural(a.name, b.name));
  if (nivel === "finca") return fincas.map((f) => ({ id: f.id, grupo: f.id }));

  const naves = [...d.naves].sort(
    (a, b) =>
      natural(fincaNombre.get(a.location_id ?? "") ?? "￿", fincaNombre.get(b.location_id ?? "") ?? "￿") ||
      natural(a.name, b.name),
  );
  if (nivel === "nave") return naves.map((n) => ({ id: n.id, grupo: n.location_id }));

  const naveRank = new Map(naves.map((n, i) => [n.id, i]));
  const naveFinca = new Map(naves.map((n) => [n.id, n.location_id]));
  const sectores = [...(d.sectores ?? [])]
    .filter((s) => naveRank.has(s.greenhouse_id))
    .sort((a, b) => (naveRank.get(a.greenhouse_id) as number) - (naveRank.get(b.greenhouse_id) as number) || natural(a.name, b.name));
  if (nivel === "sector") return sectores.map((s) => ({ id: s.id, grupo: naveFinca.get(s.greenhouse_id) ?? null }));

  const sectorRank = new Map(sectores.map((s, i) => [s.id, i]));
  const sectorFinca = new Map(sectores.map((s) => [s.id, naveFinca.get(s.greenhouse_id) ?? null]));
  return [...(d.ciclos ?? [])]
    .filter((c) => sectorRank.has(c.block_id))
    .sort(
      (a, b) =>
        (sectorRank.get(a.block_id) as number) - (sectorRank.get(b.block_id) as number) ||
        a.start_date.localeCompare(b.start_date) ||
        natural(a.crop_name, b.crop_name),
    )
    .map((c) => ({ id: c.id, grupo: sectorFinca.get(c.block_id) ?? null }));
}

// ── Nombre con código y superficie para informes ───────────────────────────

/** «Nave 1» + [«P01»] → «Nave 1 · P01». Los vacíos no cuentan. */
export function nombreConCampos(nombre: string, partes: ReadonlyArray<string | null | undefined>): string {
  const extra = partes.map((p) => (p ?? "").trim()).filter(Boolean);
  return extra.length ? `${nombre} · ${extra.join(" · ")}` : nombre;
}

export interface SectorArea {
  id: string;
  naveId: string;
  /** La del dibujo (blocks.area_m2), null si no hay. */
  areaReal: number | null;
}

/**
 * La superficie de cada sector PARA INFORMES. Si su nave tiene una superficie
 * escrita a mano, sus sectores se escalan en proporción para que la nave sume
 * exactamente esa cifra (y, sin áreas en el dibujo, se reparte a partes
 * iguales). Sin superficie a mano, la real. Las dosis NO usan esto.
 */
export function areasParaInformes(
  sectores: ReadonlyArray<SectorArea>,
  superficieNave: ReadonlyMap<string, number | null>,
): Map<string, number | null> {
  const porNave = new Map<string, SectorArea[]>();
  for (const s of sectores) {
    const l = porNave.get(s.naveId) ?? [];
    l.push(s);
    porNave.set(s.naveId, l);
  }
  const out = new Map<string, number | null>();
  for (const [naveId, lista] of porNave) {
    const aMano = superficieNave.get(naveId) ?? null;
    const reales = lista.map((s) => (s.areaReal && s.areaReal > 0 ? s.areaReal : 0));
    const suma = reales.reduce((a, b) => a + b, 0);
    lista.forEach((s, i) => {
      if (!(aMano && aMano > 0)) out.set(s.id, s.areaReal);
      else if (suma > 0) out.set(s.id, (reales[i] / suma) * aMano);
      else out.set(s.id, aMano / lista.length);
    });
  }
  return out;
}

/** La superficie de la nave para informes: la escrita a mano o la suma real. */
export function areaNaveParaInformes(aMano: number | null | undefined, areasReales: ReadonlyArray<number | null>): number {
  if (aMano && aMano > 0) return aMano;
  return areasReales.reduce<number>((a, b) => a + (b && b > 0 ? b : 0), 0);
}
